import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fleetApi } from "../api/fleet";

const CARS_QK = "fleet-cars";
const RES_QK = "fleet-reservations";

// ── Cars ──────────────────────────────────────────────────────────────────────

export function useCars(params?: Record<string, string>) {
  return useQuery({
    queryKey: [CARS_QK, params],
    queryFn: () => fleetApi.listCars(params),
    staleTime: 0,
  });
}

export function useCar(id: string) {
  return useQuery({
    queryKey: [CARS_QK, id],
    queryFn: () => fleetApi.getCar(id),
  });
}

export function useCarHistory(id: string) {
  return useQuery({
    queryKey: [CARS_QK, id, "history"],
    queryFn: () => fleetApi.getHistory(id),
    enabled: !!id,
  });
}

export function useCreateCar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fleetApi.createCar,
    onSuccess: () => qc.invalidateQueries({ queryKey: [CARS_QK] }),
  });
}

export function useUpdateCar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof fleetApi.updateCar>[1] }) =>
      fleetApi.updateCar(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CARS_QK] }),
  });
}

export function useDeleteCar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => fleetApi.deleteCar(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CARS_QK] }),
  });
}

export function useSellCar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => fleetApi.sellCar(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CARS_QK] }),
  });
}

export function useAssignCar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof fleetApi.assignCar>[1] }) =>
      fleetApi.assignCar(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CARS_QK] }),
  });
}

export function useReturnToPool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof fleetApi.returnToPool>[1] }) =>
      fleetApi.returnToPool(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CARS_QK] }),
  });
}

export function useMovePool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof fleetApi.movePool>[1] }) =>
      fleetApi.movePool(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CARS_QK] }),
  });
}

// ── Reservations ──────────────────────────────────────────────────────────────

export function useReservations(params?: Record<string, string>) {
  return useQuery({
    queryKey: [RES_QK, params],
    queryFn: () => fleetApi.listReservations(params),
    staleTime: 0,
  });
}

export function useCreateReservation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fleetApi.createReservation,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [RES_QK] });
      qc.invalidateQueries({ queryKey: [CARS_QK] });
    },
  });
}

export function useDeleteReservation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fleetApi.deleteReservation,
    onSuccess: () => qc.invalidateQueries({ queryKey: [RES_QK] }),
  });
}

// ── Issue Reports ─────────────────────────────────────────────────────────────

const ISSUES_QK = "fleet-issues";
const COMMENTS_QK = "fleet-comments";
const TODO_QK = "fleet-todo";

export function useIssueReports(params?: Record<string, string>) {
  return useQuery({
    queryKey: [ISSUES_QK, params],
    queryFn: () => fleetApi.listIssueReports(params),
    staleTime: 0,
    enabled: params !== undefined,
  });
}

export function useCreateIssueReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fleetApi.createIssueReport,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [ISSUES_QK] });
      qc.invalidateQueries({ queryKey: [TODO_QK] });
    },
  });
}

export function useUpdateIssueReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof fleetApi.updateIssueReport>[1] }) =>
      fleetApi.updateIssueReport(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [ISSUES_QK] });
      qc.invalidateQueries({ queryKey: [TODO_QK] });
    },
  });
}

export function useDeleteIssueReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fleetApi.deleteIssueReport,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [ISSUES_QK] });
      qc.invalidateQueries({ queryKey: [TODO_QK] });
    },
  });
}

// ── Comments ──────────────────────────────────────────────────────────────────

export function useIssueComments(reportId: string) {
  return useQuery({
    queryKey: [COMMENTS_QK, reportId],
    queryFn: () => fleetApi.listComments(reportId),
    enabled: !!reportId,
    staleTime: 0,
  });
}

export function useCreateComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fleetApi.createComment,
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: [COMMENTS_QK, vars.report] });
      qc.invalidateQueries({ queryKey: [ISSUES_QK] });
    },
  });
}

export function useDeleteComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reportId }: { id: string; reportId: string }) =>
      fleetApi.deleteComment(id).then(() => reportId),
    onSuccess: (reportId) => qc.invalidateQueries({ queryKey: [COMMENTS_QK, reportId] }),
  });
}

// ── Fleet TODO count (for sidebar badge) ─────────────────────────────────────

export function useFleetTodoCount(enabled = true) {
  return useQuery({
    queryKey: [TODO_QK],
    queryFn: fleetApi.getTodoCount,
    enabled,
  });
}
